// ============================================================================
// ui/config-adder.js — "Add another color" (multiple configurations of ONE product)
// ============================================================================
// A school buying white 45s and green 25s needs the SAME competition bumper twice. Ryan's shape
// (2026-08-09): a `+` that appears only AFTER the first pick, competition bumpers only, each
// configuration designed through the normal flow.
//
// ⭐ ONE COMPONENT, THREE SURFACES, and that is the whole reason this file exists rather than the
// control being written inline where it is used. The buyer can pick products in three different
// places — the desktop gate's chip row (`select-weights-step.js`), the in-tool Pick Weights
// accordion (`pick-weights-panel.js`) and the phone gate's swipe pager (`mobile-kind-pager.js`) —
// and they are three separate renderers of one decision. CLAUDE.md §0b, learned the hard way on
// the layout picker: when two places need the same answer, one of them will be wrong. The cap, the
// wording, the disabled state and the announcement are stated once here.
//
// ⚠ IT IS A COUNT, NOT A LIST, and that is deliberate. What distinguishes two configurations is
// their PLATE COLOR, which is chosen later in Customize Design — so at pick time they are not yet
// different from each other, and rendering two identical "Competition Urethane" pills would be the
// UI claiming a difference the buyer has not made. "How many colors of this do you want" is the
// honest question here; the switcher tells them apart once they carry colors.
//
// ⭐ THE CONTROL RIDES *ON* THE PILL (Ryan 2026-08-09), one circle inset at each end, and the count
// moved into the pill's own label as "Competition Urethane (2)". It used to sit in a row UNDERNEATH
// the pill, which kept both elements at full width but spent a whole extra line per comp bumper on
// a control most buyers never touch, and left the count saying "2 colors" a few pixels away from a
// name that still claimed to be one product. On the pill, the name IS the count.
//   ⚠ The buttons are SIBLINGS of the pill inside `configAnchor`, never children of it. Every
//   surface's pill is a real <button> and a nested <button> is invalid HTML the parser will hoist
//   straight back out — so they are absolutely positioned over it instead, which is also what lets
//   the same component pin itself to a 235px accordion chip and a full-width phone Select button
//   with no per-surface geometry.
//   ⚠ The pill reserves room at BOTH ends even while `−` is hidden, so the label sits still when
//   the count changes rather than sliding as a button appears beside it.
// ============================================================================

import { h } from './dom.js';
import {
  productInstances, addProductInstance, removeProductInstance, canAddProductInstance,
} from './state.js';
import { productSupportsMultiConfig, maxConfigsFor, productKey, designLadder } from '../products.js';
// The shared hover bubble — extracted OUT of this file 2026-08-10 when the Size ladder needed the
// same thing. The phone suppression, the <body> parenting and the "never also set `title`" rule all
// moved with it; see ui/tooltip.js.
import { attachTip, hideTip } from './tooltip.js';
import { displayWeightText } from '../design-profile.js';


/**
 * The relative box that a surface wraps around its own pill + this control. Stated here rather
 * than as a class name three files repeat, because the absolute positioning below only works
 * against a box whose edges ARE the pill's edges.
 */
export function configAnchor(...kids) {
  return h('span', { class: 'wd-cfg-anchor' }, ...kids);
}

/**
 * The BESIDE layout's row: a three-column grid, `[− slot] [pill] [+ slot]`, with both slots
 * reserved whether or not this particular product has a control.
 *
 * ⭐ Ryan 2026-08-09: *"then we don't have buttons on top of buttons which can be confusing to some
 * folks."* Right, and it only became affordable once the pill was allowed to get narrower — the
 * reason 258 put the control under the pill was that beside-it meant the name truncating.
 * ⚠ THE SLOTS ARE RESERVED FOR EVERY ROW IN A CATEGORY THAT HAS ANY MULTI-CONFIG PRODUCT, including
 * the rows that have no control (the Training Rubber sitting under two Competition rows). Reserve
 * only where a control exists and the pills in one open card come out at two different widths with
 * their left edges 34px apart, which reads as a layout bug rather than as an absent control.
 */
export function configRow(...kids) {
  return h('span', { class: 'wd-cfg-row' }, ...kids);
}

/**
 * @param {object} ctx  the app context (needs `.state`, optionally `.announce`)
 * @param {{category: string, kind: string, onChange?: () => void, labelEl?: HTMLElement}} opts
 *   `onChange` fires after any add/remove — the same hook the pill toggles use, so the switcher
 *   and the live 3D follow a new configuration exactly as they follow a new product.
 *   `labelEl` is the surface's OWN product-name node (the chip's `__t` span, the pager's `<h3>`).
 *   The count is written into it as "Competition Urethane (2)" — the component owns the wording so
 *   the three surfaces cannot drift, and it owns nothing else about that element.
 * @returns {{el: HTMLElement, sync: () => void}} `sync` is called by the host's own repaint; this
 *   component never schedules its own, so it can't fight a surface that rebuilds in place.
 */
export function createConfigAdder(ctx, { category, kind, onChange, labelEl, layout = 'over' } = {}) {
  const key = productKey(category, kind);
  /* Products that cannot be dyed get a component that renders nothing and costs one hidden div.
     Returning null instead would make every caller write the same `if` — three chances to forget,
     on three surfaces, for a rule that is already answered by `productSupportsMultiConfig`. */
  const supported = productSupportsMultiConfig(key);
  const cap = maxConfigsFor(key);

  /* ⭐ `.wd-step-btn` — THE APP'S OWN −/+ BUTTON, not a bespoke one. It is what the size steppers
     and the nudge pad are built from, so the glyph metrics, the hover tint, the disabled fade and
     the not-allowed cursor all arrive from rules that already exist; `.wd-cfg__btn` adds only the
     standalone surface (a stepper's two buttons share one grooved box, ours sit at opposite ends of
     a pill) and the positioning.
     ⛔ NOT flat glass, and that is a lesson this app learned earlier the same day on the ✎/+ swatch
     chips: the bevel spends a top hairline, a top glow and a bottom pool on the outer few pixels,
     which is a rim on a 200px button and a serious share of the face on a 28px one — so a small
     glass control reads as smaller and cheaper than it is. Flat field surface instead. */
  const STEP = 'wd-step-btn';

  /* ⭐ DRAWN, NOT TYPESET (Ryan 2026-08-09: the glyphs read *"ever so slightly too low… too far
     left"*, then off-centre again on mobile at a different button size). Both complaints were the
     same cause: `+` and `−` are FONT glyphs, and a font centres its em box, not its ink — the body
     face carries more side bearing on the right and seats both marks low. So a perfectly centred
     BOX paints off-centre INK, and the nudge that fixes it is a property of that glyph at that
     size, which is why it needed re-tuning the moment the button changed shape.
       An SVG stroke has no bearings. Centred by its own viewBox at any size, on any platform, with
     no per-breakpoint offset to keep in step — and the chevron on the card header above is already
     drawn this way, so it is the file's existing answer to "a small mark on a control". */
  const BAR_H = '<path d="M4 10h12" />';
  const BAR_V = '<path d="M10 4v12" />';
  const icon = (inner) => `<svg viewBox="0 0 20 20" width="100%" height="100%" fill="none"
    stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">${inner}</svg>`;

  /* ⭐ THE SITE'S OWN TOOLTIP, NOT THE BROWSER'S (Ryan 2026-08-09, pointing at the header's cart and
     quote icons). `title` is unstyleable, opens after an OS delay of roughly a second, truncates
     where it likes and cannot wrap — and this copy is now a full sentence with an example in it,
     which is exactly the kind of string a native tooltip serves worst.
       The recipe is the theme's `.header-icon-tooltip` (`assets/custom-quote.css`): #000F1C on a 1px
     white rim, 6px radius, 12px/500, and the two-triangle arrow that leaves ~1px of the outline
     showing. Ported rather than approximated, so the tool's tooltip and the storefront's are the
     same object to a buyer who has just come from the header.
     ⚠ `aria-label` still carries the text — the bubble is `aria-hidden` decoration. And `title` is
     GONE, deliberately: leaving it would show the native tooltip a second after this one, saying
     the same thing twice in two different visual languages. */
  const minus = h('button', {
    type: 'button',
    class: `wd-cfg__btn wd-cfg__btn--minus ${STEP}`,
    onClick: (e) => { stop(e); drop(); },
  }, h('span', { class: 'wd-cfg__ico', 'aria-hidden': 'true', html: icon(BAR_H) }));

  const plus = h('button', {
    type: 'button',
    class: `wd-cfg__btn wd-cfg__btn--plus ${STEP}`,
    onClick: (e) => { stop(e); add(); },
  }, h('span', { class: 'wd-cfg__ico', 'aria-hidden': 'true', html: icon(BAR_H + BAR_V) }));

  /* TWO LAYOUTS, ONE COMPONENT.
     `beside` (the Pick Weights pane) — `display: contents`, so the two buttons become grid items of
       the row itself and land in their own reserved columns either side of the pill. Nothing
       overlaps anything, which is the whole point of it.
     `over` (the gate chip row, the phone pager) — an overlay spanning the pill, with the buttons
       pinned inside its ends. ⚠ `pointer-events: none` here, `auto` on the buttons: the box covers
       the whole pill and without it the pill is unclickable everywhere except its middle.
     ⛔ The gate CANNOT take `beside`, and it is a measured fact rather than a preference: its panel
     is ~261px, two slots cost 68, and "Competition Urethane (2)" needs 169 against the 157 that
     would be left. It would truncate the product name to spare an overlap. */
  /* Hover AND keyboard focus; hidden again on click, because after a press the count has changed
     so the wording is stale until `sync()` rewrites it — and a tap on a touch device leaves a
     phantom hover that would otherwise park a tooltip on screen with no way to dismiss it. */
  [minus, plus].forEach((btn) => attachTip(btn, () => btn._wdTipText));

  const el = h('div', { class: `wd-cfg wd-cfg--${layout}`, hidden: true }, minus, plus);

  /* ⛔ THE CLICK MUST NOT REACH THE PILL. On two of the three surfaces this control sits inside the
     product's own row, and on the accordion the whole CARD HEADER is a button — so a bubbling click
     would add a configuration and then immediately toggle the product off underneath it, removing
     every configuration including the one just added. `preventDefault` too: the accordion's header
     is a real <button>, and a nested activation would still fire its default. */
  function stop(e) { e.stopPropagation(); e.preventDefault(); }

  function add() {
    const n = addProductInstance(ctx.state, category, kind);
    if (n == null) return;
    ctx.announce?.(`Added another ${kind} color. You now have ${productInstances(ctx.state, category, kind).length}.`);
    /* The reason is passed on, because a host that animates an ADD must not animate a REMOVE — and
       every existing caller ignores the argument, so this costs them nothing. */
    onChange?.('add');
    sync();
  }

  function drop() {
    /* THE HIGHEST, which is the one the `+` most recently produced. Undo should take back the last
       thing you did — and it is also the only configuration the buyer can be sure they have not yet
       designed, so it is the cheapest one to lose. */
    const used = productInstances(ctx.state, category, kind);
    if (!used.length) return;
    /* ⭐ AND IT GOES ALL THE WAY TO ZERO (Ryan 2026-08-09: *"even if there's only 1 comp bumper
       added, still include the - button.. clicking it will just remove that 1"*). It used to refuse
       below two, on the reasoning that un-picking is the pill's job — but that made `−` a control
       that appears, does its work, and then vanishes one press before the job is finished, leaving
       the buyer to notice that the LAST one comes off somewhere else. `removeProductInstance` just
       splices the row out of `selectedProducts`, so dropping the only configuration deselects the
       product exactly as toggling the pill does; `onChange` is the same hook the pill fires, so the
       switcher and the live 3D re-home identically either way. */
    const last = used.length === 1;
    removeProductInstance(ctx.state, category, kind, used[used.length - 1]);
    const left = productInstances(ctx.state, category, kind).length;
    ctx.announce?.(last ? `Removed ${kind}.` : `Removed a ${kind} color. You now have ${left}.`);
    onChange?.('remove');
    sync();
  }

  /** One place that writes the wording: the bubble AND the accessible name.
   *
   * ⚠ THEY ARE ALLOWED TO DIFFER, and where they do it is deliberate rather than drift. The bubble
   * is read by someone who can see which pill it is attached to, so "Click here to add a second
   * color" is unambiguous. A screen-reader user gets the button's name with no such context — and
   * "click here" tells them nothing about WHICH product — so the label names it. Same instruction,
   * two audiences; pass one argument when the wording suits both. */
  function setTip(btn, text, label) {
    btn.setAttribute('aria-label', label || text);
    btn._wdTipText = text;           // resolved by the shared bubble at hover time
  }

  function sync() {
    if (!supported) { el.hidden = true; return; }
    const n = productInstances(ctx.state, category, kind).length;
    // ⚠ NOTHING UNTIL THE FIRST PICK (Ryan). A `+` on an unpicked product is offering a second of
    // something they have not asked for once, and it would sit on every comp bumper pill at rest.
    el.hidden = n === 0;
    /* ⚠ THE LABEL IS RESET EVEN WHEN THE CONTROL IS GONE. Un-picking a product that carried two
       configurations hides this box, and an early return here would leave "(2)" printed on a pill
       that no longer has any — the suffix outliving the thing it counts. */
    /* ⛔ TWO SPANS, NOT A TEXT NODE — so a squeezed surface can decide WHICH HALF gives way
       (Ryan 2026-08-10: *"I tested by selecting all 4 categories… I then clicked the '+' on
       Competition Urethane. The appearance of the '+' and '-' buttons resulted in 'Competition
       Urethane (2)' wrapping so that the '(2)' appeared on the 2nd line, which pretty much broke
       it."*).
         The gate's chip is a share of a panel that is a share of the grid, so a fourth category
       takes 31px off it — while the ± reservation (36px at each end) and the count are fixed costs.
       Measured at 1440px: 188px of label room at one to three categories against a 169px label, and
       157px at four. It was always going to wrap; four categories is just where the margin ran out.
         A flat text node gives the layout no way to prefer one part over the other, so the line
       broke at the last space — putting the count, the very thing the suffix exists to say, alone on
       a second line. Named parts let the NAME ellipsise (it is still legible, and the pill is one of
       three on a card headed with its category) while the count is never what gets dropped.
       ⚠ Both spans exist at every count, so a surface's CSS never has to cope with one of them
       appearing; the count simply carries no text until there is a number worth showing. */
    if (labelEl) {
      let nameEl = labelEl.querySelector('.wd-cfg-label__name');
      let countEl = labelEl.querySelector('.wd-cfg-label__n');
      if (!nameEl || !countEl) {
        nameEl = h('span', { class: 'wd-cfg-label__name' });
        countEl = h('span', { class: 'wd-cfg-label__n' });
        labelEl.replaceChildren(nameEl, countEl);
      }
      nameEl.textContent = kind;
      countEl.textContent = n > 1 ? ` (${n})` : '';
    }
    if (!n) return;

    const many = n > 1;
    /* ⚠ VISIBLE FROM THE FIRST PICK, not from the second. See `drop` — at one configuration it
       removes the product outright, so there is no state where it would be a dead button. */

    const room = canAddProductInstance(ctx.state, category, kind);
    plus.disabled = !room;
    /* ⚠ THE CAP IS EXPLAINED, NOT JUST ENFORCED. A dead `+` with no reason reads as a broken
       button. The cap is the ladder length because configurations partition the denominations, so
       the honest sentence names that rather than quoting a number the buyer never chose. */
    /* ⚠ THE TITLE IS NOW THE ONLY WORDS. The `+` used to read "+ Add another color" at n === 1,
       which is where the whole idea gets explained — a buyer has no reason to expect a product can
       be picked twice. A circle on a pill cannot carry that sentence, so it moves here in full at
       n === 1 and shortens once the buyer has evidently understood. */
    /* Ryan's wording, 2026-08-09. The EXAMPLE is the whole point: on the welcome screen a buyer has
       never used the tool, and "add a color" reads as "add a plate to my order" — which is the
       misreading that makes a cap of 5 look like a bug to someone who wants 20 plates. A concrete
       two-weight example is what makes it land as a colour choice instead, and it stays on every
       rung rather than only the first (Ryan, second pass): the sentence is what the button MEANS,
       and someone reaching for a third colour is no more likely to have read it than someone
       reaching for a second.
         ⚠ THE ORDINAL NAMES THE COLOUR BEING ADDED, so it is `n + 1`, not `n` — pressing `+` while
       holding two colours adds the THIRD. The list runs out at the cap (five, the ladder length),
       and "another" is the backstop rather than an index error if a longer ladder ever exists. */
    const ORDINALS = ['second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth'];
    const ord = ORDINALS[n - 1] || 'another';
    if (room) {
      setTip(plus,
        `Click here to add a ${ord} color. For example, make your 45lb bumpers one color, and your 25lb bumpers another.`,
        `Add a ${ord} color of ${kind}`);
    } else {
      /* ⚠ THE WEIGHTS ARE DERIVED, NOT TYPED (Ryan 2026-08-09 gave the sentence with "10lb, 25lb,
         35lb, 45lb, and 55lb" spelled out). The cap already comes from the ladder's LENGTH, so
         naming the rungs from anything else would let the number and the list disagree the day a
         rung is added — the message would read "up to 6 colors" over five weights. Same source,
         both halves. `displayWeightText` because the buyer may be in kg, where a hard-coded "45lb"
         is simply wrong. */
      const rungs = (designLadder(key) || []).map((w) => displayWeightText(w, ctx.state?.designProfile?.unit));
      const list = rungs.length > 1
        ? `${rungs.slice(0, -1).join(', ')}, and ${rungs[rungs.length - 1]}`
        : rungs.join('');
      setTip(plus, list
        ? `You can have up to ${cap} colors, one for each weight (${list}).`
        : `You can have up to ${cap} colors, one for each weight.`);
    }
    /* The verb changes with the count, because at one configuration this is not "remove a colour"
       in any sense the buyer would recognise — it takes the product off the order. */
    /* Same split as the `+` above: "Click here…" for the eye that can see which pill it hangs off,
       the product named for the screen reader that cannot. At ONE configuration the verb changes —
       there is no colour left to remove, it takes the product off the order. */
    setTip(minus,
      many ? 'Click here to remove a color' : 'Click here to remove',
      many ? `Remove a color of ${kind}` : `Remove ${kind}`);
  }

  /* ⚠ THE SYNC HANGS OFF THE ELEMENT, so a host that REBUILDS its rows can find every live adder
     by querying the DOM instead of keeping an array. `select-weights-step` rebuilds its chip rows
     on every Back → Next cycle through the gate, and a captured array there would grow a stale
     entry per cycle, each still syncing a detached node — the exact shape of the memory
     `derived-list-in-a-const-goes-stale`. A host whose rows are genuinely built once (the Pick
     Weights accordion) can hold the objects; one that rebuilds must ask the DOM. */
  el._syncConfigAdder = sync;
  sync();
  return { el, sync };
}

/** Sync every adder inside `root`, whether or not the caller kept a reference to it. */
export function syncConfigAdders(root) {
  if (!root) return;
  root.querySelectorAll('.wd-cfg').forEach((n) => n._syncConfigAdder?.());
}
